/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React from "react";
import { useSelector } from "react-redux";
import { parseISO } from "date-fns";
import { format } from "date-fns-tz";
import styled from "styled-components";

import types from "../../helpers/types";

import { useTheme } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import EmptyCircleIcon from "@material-ui/icons/RadioButtonUnchecked";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import LockIcon from "@material-ui/icons/Lock";
import PublicIcon from "@material-ui/icons/Public";
import Grid from "@material-ui/core/Grid";
import ListItem from "@material-ui/core/ListItem";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

const StyledListItem = styled(ListItem)`
  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }
`;

const ForwardRefCircleIcon = React.forwardRef(
  ({ alignRight, theme, ...props }, ref) => (
    <CheckCircleIcon {...props} ref={ref} />
  )
);

const StyledCheckCircleIcon = styled(ForwardRefCircleIcon)`
  color: ${(props) => props.theme.palette.success.dark};
  font-size: 12px !important;
  margin: ${(props) => (props.alignRight ? "0 4px 0 0" : "0 0 0 4px")};
`;

const StyledEmptyCircleIcon = styled(({ alignRight, theme, ...props }) => (
  <EmptyCircleIcon {...props} />
))`
  color: ${(props) => props.theme.palette.grey[400]};
  font-size: 12px !important;
  margin: ${(props) => (props.alignRight ? "0 4px 0 0" : "0 0 0 4px")};
`;

const StyledNoteContents = styled(({ theme, ...props }) => (
  <Typography component='div' {...props} />
))`
  background-color: ${(props) => props.theme.palette.background.lightGrey};
  border-radius: 4px !important;
  margin: 0;
  padding: 4px 8px;
  word-break: break-all;
`;

const StyledBoldLabelTypography = styled(Typography)` 
  font-weight: 500 !important; 
`;

const StyledInfoIcon = styled(InfoOutlinedIcon)`
  font-size: 12px !important;
  margin: 0 3px !important;
`;

const SequenceReducer = (a,b) => a.sequence - b.sequence;
const returnNoteRows = (rows) => {  
  let noteArray = [...rows];
  return (noteArray.sort(SequenceReducer).map(
    (row) => (
      <Typography key={row.noteRowsId} component='div'>
        <StyledBoldLabelTypography display="inline">
          {`${row.displayLabel}: `}
        </StyledBoldLabelTypography>
        {row.contents}
      </Typography>
    )
  )
  )
};

function Note({ note, displayNoteIcon }) {
  const theme = useTheme();
  const alignRight = note.position === "right";
  const createDateTime = parseISO(note.createTime + "Z");
  const formattedCreateTime = format(createDateTime, "HH:mm (z)");
  const formattedCreateDate = format(createDateTime, "MM/dd");
  let readDateTime;
  let formattedReadTime;
  let formattedReadDate;

  if (note.isRead) {
    readDateTime = parseISO(note.readTime + "Z");
    formattedReadTime = format(readDateTime, "HH:mm (z)");
    formattedReadDate = format(readDateTime, "MM/dd");
  }

  const contents = (note.rows?.length > 0) 
    ? returnNoteRows(note.rows) 
    : note.contents;

  return (
    <StyledListItem disableGutters>
      <Grid container>
        <Grid item xs={12}>
          <Typography
            align={alignRight ? "right" : "left"}
            component="div"
            variant="caption"
          >
            {note.author} at {formattedCreateTime} on {formattedCreateDate}{" "}
            {displayNoteIcon &&
              (note.isPrivate ? (
                <LockIcon data-testid="lock-icon" fontSize="inherit" />
              ) : (
                <PublicIcon data-testid="public-icon" fontSize="inherit" />
              ))}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid
            alignItems="center"
            container
            wrap="nowrap"
            justify={alignRight ? "flex-end" : "flex-start"}
          >
            <Grid item style={{ order: alignRight ? 1 : 2 }}>
              {note.isRead ? (
                <Tooltip
                  title={`Read by ${note.readByName} on ${formattedReadDate} at ${formattedReadTime}`}
                >
                  <StyledCheckCircleIcon
                    alignRight={alignRight}
                    theme={theme}
                  />
                </Tooltip>
              ) : (
                <StyledEmptyCircleIcon alignRight={alignRight} theme={theme} />
              )}
            </Grid>
            <Grid
              item
              style={{
                order: alignRight ? 2 : 1,
              }}
            >
              <StyledNoteContents theme={theme}>
                {(note.isInfo) && (<StyledInfoIcon />)}
                {contents}
              </StyledNoteContents>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </StyledListItem>
  );
}

export default Note;
